import { useEffect, useMemo, useState, Fragment } from "react";
import { useSearchParams } from "react-router-dom";
import { safeHtml } from "@/lib/sanitize";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ClipboardList, RefreshCw, ChevronRight, Eye, RotateCw, Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import { listEmailOutbox, processOutboxNow, retryEmailOutboxRow } from "@/lib/api";
import { useViewAsClient } from "@/lib/viewAsClient";

const STATUSES = ["all", "pending", "sent", "skipped", "failed", "held"];
const LIMIT = 100;

/**
 * Administration → Email log
 * Drains the per-tenant lbs_email_outbox table. Read-only viewer with a
 * "Process queue now" trigger and per-row error inspection. Lives apart
 * from Email settings so admins can audit delivery without scrolling
 * past the SMTP form.
 */
export default function EmailLog() {
  const { viewAsClientId, setViewAsClientId, isSuperAdmin } = useViewAsClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const urlClientId = searchParams.get("client_id");
  // Bell links pass ?client_id=; move it into the visible top-right picker so the filter is obvious.
  useEffect(() => {
    if (!urlClientId) return;
    if (isSuperAdmin) setViewAsClientId(urlClientId);
    const next = new URLSearchParams(searchParams);
    next.delete("client_id");
    setSearchParams(next, { replace: true });
  }, [urlClientId, isSuperAdmin, setViewAsClientId, searchParams, setSearchParams]);
  const urlStatus = searchParams.get("status");
  const [statusFilter, setStatusFilter] = useState(STATUSES.includes(urlStatus) ? urlStatus : "all");
  useEffect(() => { if (STATUSES.includes(urlStatus)) setStatusFilter(urlStatus); }, [urlStatus]);
  const [search, setSearch] = useState("");
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const [viewing, setViewing] = useState(null); // row whose body we're inspecting
  const [tick, setTick] = useState(0);
  const refresh = () => setTick((k) => k + 1);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      // listEmailOutbox is RLS-scoped: super-admin sees all rows, the
      // global "View as <client>" picker further narrows it client-side.
      const { data } = await listEmailOutbox({ limit: LIMIT });
      if (cancelled) return;
      setRows(data || []);
    })();
    return () => { cancelled = true; };
  }, [tick, viewAsClientId]);

  const filtered = useMemo(() => {
    let out = rows;
    if (viewAsClientId) out = out.filter((r) => r.client_id === viewAsClientId);
    if (statusFilter !== "all") out = out.filter((r) => r.status === statusFilter);
    if (search.trim()) {
      // Search across recipient, subject, template, booking reference in
      // context, and last_error. Case-insensitive substring match.
      const q = search.trim().toLowerCase();
      out = out.filter((r) =>
        (r.to_email || "").toLowerCase().includes(q) ||
        (r.subject || "").toLowerCase().includes(q) ||
        (r.template_key || "").toLowerCase().includes(q) ||
        (r.last_error || "").toLowerCase().includes(q) ||
        (r.context?.reference_number || "").toLowerCase().includes(q)
      );
    }
    return out;
  }, [rows, statusFilter, viewAsClientId, search]);

  const counts = useMemo(() => {
    const c = { pending: 0, sent: 0, skipped: 0, failed: 0, held: 0 };
    const source = viewAsClientId ? rows.filter((r) => r.client_id === viewAsClientId) : rows;
    for (const r of source) {
      if (c[r.status] !== undefined) c[r.status] += 1;
    }
    return c;
  }, [rows, viewAsClientId]);

  const onProcess = async () => {
    setBusy(true);
    const { data, error } = await processOutboxNow(50);
    setBusy(false);
    if (error) {
      toast.error(error.message);
      return;
    }
    toast.success(`${data.sent} sent · ${data.skipped} skipped · ${data.failed} failed`);
    refresh();
  };

  const onRetry = async (row) => {
    const { error } = await retryEmailOutboxRow(row.id);
    if (error) { toast.error(error.message); return; }
    toast.success(`Requeued ${row.template_key} → ${row.to_email}`);
    refresh();
  };

  return (
    <div data-testid="email-log-page" className="space-y-4">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight flex items-center gap-2">
          <ClipboardList className="h-6 w-6" /> Email log
        </h1>
        <p className="text-sm text-slate-500">
          Last {LIMIT} outbox rows. Failures show the upstream Resend / SMTP
          error so you can diagnose delivery issues without leaving the page.
          {viewAsClientId && " Filtered to your selected client (top-right picker)."}
        </p>
      </div>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
          <div className="flex items-center gap-2">
            <CardTitle className="text-base">Outbox</CardTitle>
            <div className="flex items-center gap-1.5 text-xs">
              <Badge variant="secondary" className="bg-amber-100 text-amber-800" data-testid="count-pending">
                {counts.pending} pending
              </Badge>
              <Badge variant="secondary" className="bg-emerald-100 text-emerald-700" data-testid="count-sent">
                {counts.sent} sent
              </Badge>
              <Badge variant="secondary" className="bg-rose-100 text-rose-700" data-testid="count-failed">
                {counts.failed} failed
              </Badge>
              <Badge variant="secondary" className="bg-slate-100 text-slate-500" data-testid="count-skipped">
                {counts.skipped} skipped
              </Badge>
              {counts.held > 0 && (
                <Badge variant="secondary" className="bg-violet-100 text-violet-700" data-testid="count-held">
                  {counts.held} held
                </Badge>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="relative">
              <Search className="h-3.5 w-3.5 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search booking ref, email, template, error…"
                className="h-9 w-64 text-xs pl-8"
                data-testid="email-log-search"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-36 h-9 text-xs" data-testid="email-log-status-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {STATUSES.map((s) => (
                  <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button variant="outline" size="sm" onClick={refresh} data-testid="email-log-refresh">
              <RefreshCw className="h-4 w-4" />
            </Button>
            <Button onClick={onProcess} disabled={busy} size="sm" data-testid="email-log-process">
              <RefreshCw className={`h-4 w-4 mr-1.5 ${busy ? "animate-spin" : ""}`} />
              Process queue now
            </Button>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-8" />
                <TableHead>Template</TableHead>
                <TableHead>Recipient</TableHead>
                <TableHead>Subject</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Attempts</TableHead>
                <TableHead>Created</TableHead>
                <TableHead>Sent</TableHead>
                <TableHead className="w-12 text-right">View</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} className="py-10 text-center text-sm text-slate-400">
                    No rows for filter “{statusFilter}”.
                  </TableCell>
                </TableRow>
              ) : (
                filtered.map((r) => {
                  const isOpen = expanded === r.id;
                  const hasDetail = !!(r.last_error || r.subject || r.context?.changed_summary || r.context?.reference_number);
                  return (
                    <Fragment key={r.id}>
                      <TableRow
                        data-testid={`outbox-row-${r.id}`}
                        className={hasDetail ? "cursor-pointer" : ""}
                        onClick={() => hasDetail && setExpanded(isOpen ? null : r.id)}
                      >
                        <TableCell className="py-2">
                          {hasDetail && (
                            <ChevronRight
                              className={`h-4 w-4 text-slate-400 transition-transform ${isOpen ? "rotate-90" : ""}`}
                            />
                          )}
                        </TableCell>
                        <TableCell className="text-xs font-mono">{r.template_key}</TableCell>
                        <TableCell className="text-xs">{r.to_email}</TableCell>
                        <TableCell className="text-xs truncate max-w-[280px]">{r.subject || "—"}</TableCell>
                        <TableCell>
                          <Badge variant="secondary" className={statusClass(r.status)}>
                            {r.status}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-xs tabular-nums">{r.attempts ?? 0}</TableCell>
                        <TableCell className="text-xs">{fmt(r.created_at)}</TableCell>
                        <TableCell className="text-xs">{r.sent_at ? fmt(r.sent_at) : "—"}</TableCell>
                        <TableCell className="text-right">
                          {(r.status === "failed" || r.status === "skipped") && (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-7 w-7 mr-0.5"
                              onClick={(e) => { e.stopPropagation(); onRetry(r); }}
                              data-testid={`outbox-retry-${r.id}`}
                              title="Requeue for send"
                            >
                              <RotateCw className="h-4 w-4" />
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7"
                            onClick={(e) => { e.stopPropagation(); setViewing(r); }}
                            data-testid={`outbox-view-${r.id}`}
                            title="View rendered email"
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                      {isOpen && (
                        <TableRow className="bg-slate-50/60">
                          <TableCell />
                          <TableCell colSpan={8} className="text-xs">
                            {r.last_error && (
                              <div className="mb-2">
                                <div className="font-semibold text-rose-700 mb-0.5">Last error</div>
                                <pre className="whitespace-pre-wrap break-words text-rose-700 bg-rose-50 border border-rose-200 rounded p-2">
                                  {r.last_error}
                                </pre>
                              </div>
                            )}
                            {r.context?.changed_summary && (
                              <div className="mb-2">
                                <div className="font-semibold text-slate-600 mb-0.5">What changed</div>
                                <div className="bg-slate-100 border-l-2 border-slate-800 px-3 py-1.5 rounded text-slate-700">
                                  {r.context.changed_summary}
                                </div>
                              </div>
                            )}
                            {r.context?.reference_number && (
                              <div className="mb-1">
                                <span className="font-semibold text-slate-600">Booking:</span>{" "}
                                <span className="font-mono text-slate-700">{r.context.reference_number}</span>
                              </div>
                            )}
                            {r.subject && (
                              <div>
                                <span className="font-semibold text-slate-600">Subject:</span>{" "}
                                <span className="text-slate-700">{r.subject}</span>
                              </div>
                            )}
                          </TableCell>
                        </TableRow>
                      )}
                    </Fragment>
                  );
                })
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={!!viewing} onOpenChange={(o) => !o && setViewing(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-hidden flex flex-col" data-testid="email-view-dialog">
          <DialogHeader>
            <DialogTitle className="text-base">{viewing?.subject || "(no subject)"}</DialogTitle>
            <div className="text-xs text-slate-500 flex flex-wrap gap-x-4 gap-y-0.5 pt-1">
              <span><span className="text-slate-400">To</span> {viewing?.to_email}</span>
              <span><span className="text-slate-400">Template</span> <span className="font-mono">{viewing?.template_key}</span></span>
              <span><span className="text-slate-400">Status</span> {viewing?.status}</span>
              <span><span className="text-slate-400">Sent</span> {viewing?.sent_at ? fmt(viewing.sent_at) : "—"}</span>
            </div>
          </DialogHeader>
          <div className="flex-1 overflow-auto -mx-6 px-6 pt-2">
            {viewing?.body_html ? (
              <div className="rounded border bg-white p-4">
                <div dangerouslySetInnerHTML={safeHtml(viewing.body_html)} />
              </div>
            ) : viewing?.body_text ? (
              <pre className="whitespace-pre-wrap text-xs bg-slate-50 border rounded p-3">
                {viewing.body_text}
              </pre>
            ) : (
              <div className="text-sm text-slate-400 py-10 text-center">
                Body not stored for this row (pre-Resend rows or skipped templates).
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function statusClass(s) {
  if (s === "sent") return "bg-emerald-100 text-emerald-700";
  if (s === "failed") return "bg-rose-100 text-rose-700";
  if (s === "skipped") return "bg-slate-100 text-slate-500";
  return "bg-amber-100 text-amber-800";
}

function fmt(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("en-AU", {
    month: "short", day: "2-digit", hour: "2-digit", minute: "2-digit", hour12: false,
  });
}
